import { useState, type ReactNode } from 'react'
import { useFeature } from '../../add-ons/index.js'
import { useFeatures, useRosterRowTabs, type FeatureWebModule, type RosterContext, type RosterRowTabContribution, segmentedTabClass, TabStrip } from '../../core/index.js'
import { WorkerDetailPanel } from './WorkerDetailPanel.js'
import { WorkerRecordPanel } from './WorkerRecordPanel.js'

const DETAILS_TAB = 'details'
const SCORING_TAB = 'scoring'
const NO_BADGE = (): number => 0

/** One add-on's tab button: shown only while the client's agreement includes the add-on, so a tab never advertises what the client does not have. */
function FeatureTabButton({ feature, facilityId, active, onSelect }: { feature: FeatureWebModule; facilityId: string; active: boolean; onSelect: () => void }) {
  const standing = useFeature(facilityId, feature.key)
  if (!feature.workerTab || standing.isLoading || !standing.licensed) {
    return null
  }
  return <TabButton label={standing.enabled ? feature.workerTab.label : `${feature.workerTab.label} · Off`} active={active} onSelect={onSelect} />
}

/** A module's tab button, wearing whatever count the module reads for it (the Messages tab's notes not yet seen). */
function ModuleTabButton({ tab, workerId, active, onSelect }: { tab: RosterRowTabContribution; workerId: string; active: boolean; onSelect: () => void }) {
  const useBadge = tab.useBadge ?? NO_BADGE
  const count = useBadge(workerId)
  return <TabButton label={tab.label} active={active} onSelect={onSelect} count={count} />
}

function TabButton({ label, active, onSelect, count = 0 }: { label: string; active: boolean; onSelect: () => void; count?: number }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onSelect}
      className={segmentedTabClass(active)}
    >
      {label}
      {count > 0 && (
        <span className="ml-1.5 inline-block rounded-full px-1.5 text-[10px] font-bold align-middle bg-coral text-white" aria-label={`${count} not yet seen`}>
          {count}
        </span>
      )}
    </button>
  )
}

/**
 * An open roster row's body in tabs: Details, the record the Edit worker
 * dialog edits with the worker's login and invitation (opened first, so
 * expanding a worker shows the same record the dialog does); Scoring, the
 * worker's scoring panel as it always was; one tab per add-on that contributes a worker view,
 * such as Performance; and one per module that contributes a tab
 * (src/webModules.ts), such as the messages module's Messages, this
 * worker's own notes about what is getting in the way. An add-on's tab
 * gates its own body, so a facility with the add-on switched off sees the
 * tab marked Off and the add-on's own note inside it.
 */
interface WorkerRowTabsProps {
  workerId: string
  roster: RosterContext
  /** The tab to open on; the row is remounted to ask for another. */
  initialTab?: string
  /** The client the worker's login is read under; undefined until the session names it. */
  clientId: string | undefined
  /** Opens the Edit worker dialog from the Details tab; null where the reader may not edit. */
  onEdit: (() => void) | null
}

export function WorkerRowTabs({ workerId, roster, initialTab = DETAILS_TAB, clientId, onEdit }: WorkerRowTabsProps) {
  const [active, setActive] = useState<string>(initialTab)
  const features = useFeatures()
  const moduleTabs = useRosterRowTabs()
  const activeFeature = features.find((feature) => feature.key === active && feature.workerTab)
  const activeModuleTab = moduleTabs.find((tab) => tab.key === active)
  let body: ReactNode
  if (activeFeature?.workerTab) {
    body = <activeFeature.workerTab.panel workerId={workerId} {...roster} />
  } else if (activeModuleTab) {
    body = <activeModuleTab.panel workerId={workerId} {...roster} />
  } else if (active === SCORING_TAB) {
    body = <WorkerDetailPanel workerId={workerId} />
  } else {
    body = <WorkerRecordPanel workerId={workerId} facilityId={roster.facilityId} clientId={clientId} onEdit={onEdit} />
  }
  return (
    <div className="mx-4 mb-4 overflow-hidden rounded-lg border border-marine/20 bg-white shadow-sm">
      <div className="px-3 pt-3">
      <TabStrip activeKey={active}>
        <TabButton label="Details" active={active === DETAILS_TAB} onSelect={() => setActive(DETAILS_TAB)} />
        <TabButton label="Scoring" active={active === SCORING_TAB} onSelect={() => setActive(SCORING_TAB)} />
        {features.map((feature) => (
          <FeatureTabButton key={feature.key} feature={feature} facilityId={roster.facilityId} active={active === feature.key} onSelect={() => setActive(feature.key)} />
        ))}
        {moduleTabs.map((tab) => (
          <ModuleTabButton key={tab.key} tab={tab} workerId={workerId} active={active === tab.key} onSelect={() => setActive(tab.key)} />
        ))}
      </TabStrip>
      </div>
      {body}
    </div>
  )
}
